import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Contact, Loader2 } from 'lucide-react';
import type { GuardianEstado } from 'shared';
import { useGuardians } from '@/hooks/useGuardians';
import { formatDay, GUARDIAN_FILTERS } from '@/lib/guardianView';
import { ErrorBlock, LoadingBlock } from '@/components/activities/ActivityUi';
import { GuardianEstadoBadge } from '@/components/guardians/GuardianEstadoBadge';
import GuardianDetailModal from '@/components/guardians/GuardianDetailModal';
import { useToast } from '@/components/useToast';
import { ToastContainer } from '@/components/Toast';

const EMPTY_COPY: Record<GuardianEstado | '', string> = {
  Pendente: 'Nenhum registo à espera de aprovação.',
  Ativo: 'Ainda não há contas ativas.',
  Inativo: 'Nenhuma conta desativada.',
  '': 'Ainda ninguém se registou como Encarregado de Educação.',
};

/**
 * Encarregados de Educação: approving the accounts registered in the app, and
 * linking each one to the participants they look after.
 */
export default function EncarregadosPage() {
  const { toasts, toast, dismiss } = useToast();
  const [estado, setEstado] = useState<GuardianEstado | ''>('Pendente');
  // ?id= opens that guardian straight away, as the privacy requests link.
  const [searchParams] = useSearchParams();
  const [openId, setOpenId] = useState<number | null>(() => {
    const id = Number(searchParams.get('id'));
    return Number.isInteger(id) && id > 0 ? id : null;
  });

  const { data, isLoading, isError, isFetching } = useGuardians(estado);
  const guardians = data?.guardians ?? [];

  const notice = (message: string, variant: 'success' | 'error') =>
    variant === 'success' ? toast.success(message) : toast.error(message);

  return (
    <>
      <Helmet>
        <title>Encarregados de Educação | DIGGER</title>
      </Helmet>

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Contact className="w-6 h-6 text-primary" />
            Encarregados de Educação
          </h1>
          <p className="text-muted-foreground mt-1">
            Quem se regista na app como Encarregado de Educação só entra depois de aprovado aqui, e
            só vê os participantes que associares à conta.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div
            className="flex items-center gap-1 p-1 rounded-md border border-border bg-muted/30"
            role="group"
            aria-label="Filtrar por estado"
          >
            {GUARDIAN_FILTERS.map((f) => (
              <button
                key={f.estado || 'all'}
                type="button"
                onClick={() => setEstado(f.estado)}
                aria-pressed={estado === f.estado}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  estado === f.estado
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {guardians.length > 0 && (
            <span className="text-sm text-muted-foreground tabular-nums ml-auto">
              {guardians.length} conta{guardians.length !== 1 ? 's' : ''}
              {isFetching && !isLoading && (
                <Loader2 className="inline-block w-3 h-3 ml-1.5 animate-spin text-primary" />
              )}
            </span>
          )}
        </div>

        <div className="card overflow-hidden">
          {isLoading && <LoadingBlock />}
          {isError && <ErrorBlock message="Não foi possível carregar os Encarregados de Educação." />}

          {!isLoading && !isError && guardians.length === 0 && (
            <p className="py-16 text-center text-sm text-muted-foreground">{EMPTY_COPY[estado]}</p>
          )}

          {!isLoading && !isError && guardians.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm" aria-label="Lista de Encarregados de Educação">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    {['Nome', 'Email', 'Telemóvel', 'Registo', 'Educandos', 'Estado'].map((h) => (
                      <th key={h} scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {guardians.map((g) => {
                    const name = `${g.nome} ${g.apelido}`;
                    return (
                      <tr
                        key={g.id}
                        className={`hover:bg-accent/30 transition-colors ${
                          g.estado === 'Pendente' ? 'bg-yellow-50/60 dark:bg-yellow-900/10' : ''
                        }`}
                      >
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => setOpenId(g.id)}
                            className="font-medium text-foreground hover:text-primary hover:underline text-left"
                            aria-label={`Abrir ${name}`}
                          >
                            {name}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground break-all">{g.email}</td>
                        <td className="px-4 py-3 text-muted-foreground">{g.telemovel}</td>
                        <td className="px-4 py-3 text-muted-foreground tabular-nums">{formatDay(g.createdAt)}</td>
                        <td className="px-4 py-3 text-muted-foreground tabular-nums">{g.educandos}</td>
                        <td className="px-4 py-3">
                          <GuardianEstadoBadge estado={g.estado} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {openId !== null && (
        <GuardianDetailModal guardianId={openId} onClose={() => setOpenId(null)} onNotice={notice} />
      )}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </>
  );
}
